import {useState} from 'react'

export interface propertyEditorProps<
  InterfaceType extends Record<string, any>,
  RelatedItems extends Record<string, any>
> {
  props: Record<string, 'string' | 'string[]' | 'boolean' | 'number'>
  action: (id: string | null, update: Partial<InterfaceType>, related: RelatedItems) => void
  related: RelatedItems
}

export interface PropertyEditor<
  InterfaceType extends Record<string, any>,
  RelatedItems extends Record<string, any> = Record<string, never>
> {
  obj: InterfaceType | null
  related: RelatedItems
  id: string | null
  createMode: boolean
  updateMode: boolean
  getString: (field: string) => string | undefined | null
  setString: (field: string, val: string | undefined | null) => void
  getBoolean: (field: string) => boolean | undefined | null
  setBoolean: (field: string, val: boolean | undefined | null) => void
  getStringArray: (field: string) => string[] | undefined | null
  setStringArray: (field: string, val: string[] | undefined | null) => void
  addToStringArray: (field: string, val: string) => void
  removeFromStringArray: (field: string, val: string) => void
  toggle: (field: string) => void
  getNumber: (field: string) => number | undefined | null
  setNumber: (field: string, val: number | undefined | null) => void
  reset: (field: string) => void
  start: (obj: InterfaceType, related: RelatedItems) => void
  cancel: () => void
  confirm: () => void
}

export function usePropertyEditor<
  InterfaceType extends Record<string, any>,
  RelatedItems extends Record<string, any> = Record<string, never>
>(
  props: propertyEditorProps<InterfaceType, RelatedItems>
): PropertyEditor<InterfaceType, RelatedItems> {
  const [obj, setObj] = useState<InterfaceType | null>(null)
  const [related, setRelated] = useState<RelatedItems>(props.related)
  const [objData, setObjData] = useState<Partial<InterfaceType>>({})
  const [modified, setModified] = useState<string[]>([])

  if (obj && obj.id !== undefined && obj.id !== null && typeof obj.id !== 'string') {
    throw new Error(`Invalid id type: Expected string or null, got: ${obj.id}`)
  }

  function validateType(val: unknown, expectedType: string, sig: string): void {
    if (val !== undefined && val !== null) {
      let failed = false
      if (expectedType === 'string[]') {
        if (!Array.isArray(val)) {
          failed = true
        } else {
          for (const item of val) {
            if (typeof item !== 'string') {
              failed = true
            }
          }
        }
      } else {
        failed = typeof val !== expectedType
      }
      if (failed) {
        throw new Error(
          `${sig}: Expected ${expectedType}, got ${typeof val}: ${JSON.stringify(val)}`
        )
      }
    }
  }

  const getValue = (field: string, expectedType: string, callerName: string) => {
    if (!obj) {
      throw new Error(`Property editor cannot ${callerName}("${field}"): Source object not found`)
    }
    if (props.props[field]) {
      if (props.props[field] !== expectedType) {
        throw new Error(
          `Cannot ${callerName}("${field}"): "${field}" expects type ${props.props[field]}`
        )
      }
      console.log(JSON.stringify(objData, null, 2))
      const val = objData[field]
      validateType(val, expectedType, `Cannot ${callerName}("${field}")`)
      return val
    } else {
      throw new Error(`Cannot ${callerName}("${field}"): "${field}" is not a specified property`)
    }
  }
  const getBoolean = (field: string) =>
    getValue(field, 'boolean', 'getBoolean') as boolean | undefined | null
  const getString = (field: string) =>
    getValue(field, 'string', 'getString') as string | undefined | null
  const getStringArray = (field: string) =>
    getValue(field, 'string[]', 'getStringArray') as string[] | undefined | null
  const getNumber = (field: string) =>
    getValue(field, 'number', 'getNumber') as number | undefined | null

  const setValue = (
    field: string,
    val: boolean | string | string[] | number | undefined | null,
    expectedType: string,
    callerName: string
  ) => {
    if (!obj) {
      throw new Error(`Property editor cannot ${callerName}("${field}"): Source object not found`)
    }
    if (props.props[field]) {
      if (objData[field] === undefined || JSON.stringify(val) !== JSON.stringify(objData[field])) {
        if (props.props[field] !== expectedType) {
          throw new Error(
            `Cannot ${callerName}("${field}"): "${field}" expects type ${props.props[field]}`
          )
        }
        validateType(val, expectedType, `Cannot ${callerName}("${field}")`)
        setObjData({...objData, [field]: val})
        const changed = JSON.stringify(obj[field]) !== JSON.stringify(val)
        if (changed && !modified.includes(field)) {
          setModified([...modified, field])
        }
        if (!changed && modified.includes(field)) {
          setModified(modified.filter((x) => x !== field))
        }
      }
    } else {
      throw new Error(`Cannot ${callerName}("${field}"): "${field}" is not a specified property`)
    }
  }
  const setBoolean = (field: string, val: boolean | undefined | null) => {
    setValue(field, val, 'boolean', 'setBoolean')
  }
  const toggle = (field: string) => {
    const val = getValue(field, 'boolean', 'toggle')
    setValue(field, !val, 'boolean', 'toggle')
  }
  const setString = (field: string, val: string | undefined | null) => {
    setValue(field, val, 'string', 'setString')
  }
  const setStringArray = (field: string, val: string[] | undefined | null) => {
    setValue(field, val, 'string[]', 'setStringArray')
  }
  const addToStringArray = (field: string, val: string) => {
    const arr = getStringArray(field)
    const newArr = []
    let done = false
    if (arr !== undefined && arr !== null) {
      for (const item of arr) {
        newArr.push(item)
        if (item === val) {
          done = true
        }
      }
    }
    if (!done) {
      newArr.push(val)
    }
    setStringArray(field, newArr)
  }
  const removeFromStringArray = (field: string, val: string) => {
    const arr = getStringArray(field)
    const newArr = []
    if (arr !== undefined && arr !== null) {
      for (const item of arr) {
        if (item !== val) {
          newArr.push(item)
        }
      }
      setStringArray(field, newArr)
    }
  }
  const setNumber = (field: string, val: number | undefined | null) => {
    setValue(field, val, 'number', 'setNumber')
  }

  return {
    obj: obj,
    related: related,
    id: obj?.id ?? null,
    createMode: !obj?.id,
    updateMode: !!obj?.id,
    getBoolean,
    setBoolean,
    toggle,
    getString,
    setString,
    getStringArray,
    setStringArray,
    addToStringArray,
    removeFromStringArray,
    getNumber,
    setNumber,
    reset(field: string) {
      if (!obj) {
        throw new Error(`Property editor cannot reset("${field}"): Source object not found.`)
      }
      if (props.props[field]) {
        const updatedObjData: Partial<InterfaceType> = {}
        for (const prop in objData) {
          if (prop !== field) {
            updatedObjData[prop] = objData[prop]
          } else {
            if (typeof obj[prop] === props.props[field]) {
              updatedObjData[prop] = obj[prop]
            }
          }
        }
        setObjData(updatedObjData)
        setModified(modified.filter((x) => x !== field))
      } else {
        throw new Error(`Cannot reset("${field}"): "${field}" is not a specified property.`)
      }
    },
    start(obj: InterfaceType, related: RelatedItems) {
      setObj(obj)
      setRelated(related)
      const data: Partial<InterfaceType> = {}
      for (const prop in props.props) {
        console.log(`Checking ${prop}...`)
        // I'm sure there is some ingenious way to do this with generics, but my mastery of Typescript is inadequate for that.
        // So we do runtime type safety checks instead.
        if (obj[prop] !== undefined && obj[prop] !== null) {
          let failed = false
          if (props.props[prop] === 'string[]') {
            if (!Array.isArray(obj[prop])) {
              failed = true
            } else {
              for (const item of obj[prop]) {
                if (typeof item !== 'string') {
                  failed = true
                }
              }
            }
          } else {
            failed = typeof obj[prop] !== props.props[prop]
          }
          if (failed) {
            throw new Error(
              `Invalid "${prop}" type: Expected ${props.props[prop]}, got ${typeof obj[
                prop
              ]}: ${JSON.stringify(obj[prop])}`
            )
          } else {
            ;(data as Record<string, any>)[prop] = obj[prop]
          }
        }
      }
      setObjData(data)
      setModified([])
    },
    cancel() {
      setObj(null)
      setObjData({})
      setModified([])
    },
    confirm() {
      const update: Partial<InterfaceType> = {}
      for (const field of modified) {
        ;(update as Record<string, any>)[field] = objData[field]
      }
      props.action(obj?.id ?? null, update, related)
      setObj(null)
      setObjData({})
      setModified([])
    },
  }
}
